/**
 * @deprecated Use trackingService com TransistorSoft em vez deste hook
 * Este arquivo será removido em versão futura
 */
import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { logger } from '@/lib/logger';

interface Position {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
  };
  timestamp: number;
}

interface PermissionStatus {
  location: 'granted' | 'denied' | 'prompt';
  coarseLocation: 'granted' | 'denied' | 'prompt';
}

interface GeolocationState {
  position: Position | null;
  error: string | null;
  loading: boolean;
  permissions: PermissionStatus | null;
}

export const useNativeGeolocation = () => {
  const [state, setState] = useState<GeolocationState>({
    position: null,
    error: null,
    loading: false,
    permissions: null
  });

  const isNative = Capacitor.isNativePlatform();

  const checkPermissions = useCallback(async () => {
    try {
      // TransistorSoft não expõe checkPermissions, assume concedido se configurado
      const permissions: PermissionStatus = {
        location: 'granted',
        coarseLocation: 'granted'
      };
      setState(prev => ({ ...prev, permissions }));
      return permissions;
    } catch (error) {
      logger.error('Error checking geolocation permissions', error, { hook: 'useNativeGeolocation', action: 'checkPermissions' });
      return null;
    }
  }, []);

  const requestPermissions = useCallback(async () => {
    try {
      // TransistorSoft gerencia permissões automaticamente via ready()
      const permissions: PermissionStatus = {
        location: 'granted',
        coarseLocation: 'granted'
      };
      setState(prev => ({ ...prev, permissions }));
      return permissions;
    } catch (error) {
      logger.error('Error requesting geolocation permissions', error, { hook: 'useNativeGeolocation', action: 'requestPermissions' });
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to request location permissions' 
      }));
      return null;
    }
  }, []);

  const getCurrentPosition = useCallback(async (options?: PositionOptions) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const location = await BackgroundGeolocation.getCurrentPosition({
        timeout: options?.timeout ?? 30,
        maximumAge: options?.maximumAge ?? 5000,
        desiredAccuracy: options?.enableHighAccuracy ? 10 : 50,
        samples: 1
      });

      const position: Position = {
        coords: {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
          accuracy: location.coords.accuracy,
          altitude: location.coords.altitude,
          altitudeAccuracy: location.coords.altitude_accuracy,
          heading: location.coords.heading,
          speed: location.coords.speed
        },
        timestamp: new Date(location.timestamp).getTime()
      };

      setState(prev => ({ 
        ...prev, 
        position, 
        loading: false 
      }));

      return position;
    } catch (error: any) {
      logger.error('Error getting current position', error, { hook: 'useNativeGeolocation', action: 'getCurrentPosition', options });
      setState(prev => ({ 
        ...prev, 
        error: error.message || 'Failed to get location',
        loading: false 
      }));
      return null;
    }
  }, []);

  const watchPosition = useCallback(async (
    callback: (position: Position) => void,
    options?: PositionOptions
  ) => {
    try {
      logger.warn('watchPosition não implementado - use trackingService para tracking contínuo', { hook: 'useNativeGeolocation', action: 'watchPosition' });
      return null;
    } catch (error: any) {
      logger.error('Error watching position', error, { hook: 'useNativeGeolocation', action: 'watchPosition', options });
      setState(prev => ({ 
        ...prev, 
        error: error.message || 'Failed to watch location' 
      }));
      return null;
    }
  }, []);

  const clearWatch = useCallback(async (watchId: string | null) => {
    try {
      // Não implementado - use trackingService
      logger.warn('clearWatch não implementado - use trackingService', { hook: 'useNativeGeolocation', action: 'clearWatch', watchId });
    } catch (error) {
      logger.error('Error clearing watch', error, { hook: 'useNativeGeolocation', action: 'clearWatch', watchId });
    }
  }, []);

  return {
    ...state,
    isNative,
    checkPermissions,
    requestPermissions,
    getCurrentPosition,
    watchPosition,
    clearWatch
  };
};
